import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, Maximize2, BedDouble, Wifi, Wind, Tv, Coffee, Check, Calendar, Refrigerator, CookingPot } from 'lucide-react';
import { formatCurrency, getImageUrl } from '../utils/helpers';
import { usePageTitle } from '../hooks/usePageTitle';
import DatePicker from 'react-datepicker';
import { addDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

// --- IMPOR DATABASE STATIS ---
import roomsDataRaw from '../data/rooms.json';
const roomsList = Array.isArray(roomsDataRaw) ? roomsDataRaw : (roomsDataRaw?.default || []);

const AMENITY_MAP = { 
  wifi: Wifi, ac: Wind, tv: Tv, coffee: Coffee, fridge: Refrigerator, 
  kettle: Coffee, wardrobe: Maximize2, linen: BedDouble, terrace: Coffee, 
  balcony: Coffee, kitchen: CookingPot 
};

// --- LOGIKA HARGA DINAMIS ---
const getSeasonMultiplier = (date) => {
  const month = date.getMonth(); 
  const day = date.getDate();
  if (month === 2 && day >= 17 && day <= 25) return 1.303; 
  if (month === 6 || month === 7) return 1.303; 
  if (month === 11 && day >= 23 && day <= 31) return 1.303; 
  if (month === 1 && day >= 14 && day <= 18) return 1.1515; 
  if (month === 4 && (day >= 14 && day <= 17 || day >= 27 && day <= 31)) return 1.1515; 
  if (month === 5 || month === 8) return 1.1515; 
  return 1.0; 
};

const calculateTotalStayPrice = (basePrice, checkinDate, checkoutDate) => {
  let total = 0;
  let currentDate = new Date(checkinDate);
  currentDate.setHours(0, 0, 0, 0);
  let end = new Date(checkoutDate);
  end.setHours(0, 0, 0, 0);
  while (currentDate < end) {
    const rawPrice = basePrice * getSeasonMultiplier(currentDate);
    total += Math.round(rawPrice / 1000) * 1000;
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return total;
};

export default function RoomDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // 1. Tentukan Room
  const room = roomsList.find((r) => r.slug === slug) || roomsList[0];

  // 2. Set State
  const [localCheckin, setLocalCheckin] = useState(new Date());
  const [localCheckout, setLocalCheckout] = useState(addDays(new Date(), 1));
  const [selectedImage, setSelectedImage] = useState(null); // Menyimpan gambar yang diklik

  // 3. Set Title
  const name = room ? (lang === 'zh' ? (room.name_zh || room.name_id) : (lang === 'en' ? room.name_en : room.name_id)) : 'Room Detail';
  usePageTitle(name);

  if (!room) return null;

  // 4. Perhitungan
  const nights = Math.max(1, Math.round((localCheckout - localCheckin) / (1000 * 60 * 60 * 24)));
  const dynamicTotal = calculateTotalStayPrice(room.price_per_night, localCheckin, localCheckout);
  const averagePerNight = Math.round((dynamicTotal / nights) / 1000) * 1000;
  
  const currentMultiplier = getSeasonMultiplier(localCheckin);
  const isHolidaySeason = currentMultiplier > 1.0;
  const isLowSeason = currentMultiplier < 1.0;

  const description = lang === 'zh' ? (room.description_zh || room.description_id) : (lang === 'en' ? room.description_en : room.description_id);
  const bedType = lang === 'zh' ? (room.bed_type_zh || room.bed_type_id) : (lang === 'en' ? room.bed_type_en : room.bed_type_id);
  
  const features = lang === 'zh' 
    ? ["独立建筑", "直接进入花园", "户外活动座椅区", "独立卫生间", "洗漱用品", "热水供应", "毛巾和床单"]
    : (lang === 'en' ? ["Separate building", "Direct garden access", "Outdoor seating area", "Private bathroom", "Toiletries", "Hot water", "Towels & linen"] : ["Bangunan terpisah", "Akses taman langsung", "Area duduk outdoor", "Kamar mandi dalam", "Perlengkapan mandi", "Air panas", "Handuk & linen"]);

  // 5. Fungsi CTA WhatsApp Multibahasa
  const handleWhatsAppBooking = (e) => {
    e.preventDefault();
    const phoneNumber = "6281378575758"; 
    
    const formatDate = (date, locale) => date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    const guestLocale = lang === 'id' ? 'id-ID' : (lang === 'zh' ? 'zh-CN' : 'en-US');
    
    const inDateGuest = formatDate(localCheckin, guestLocale);
    const outDateGuest = formatDate(localCheckout, guestLocale);
    const inDateAdmin = formatDate(localCheckin, 'id-ID');
    const outDateAdmin = formatDate(localCheckout, 'id-ID');

    const adminCopy = `\n\n====================\n*Pesan Otomatis untuk Admin:*\nTamu di atas berminat mereservasi kamar:\n*Tipe Kamar:* ${room.name_id}\n*Jadwal:* ${inDateAdmin} s.d. ${outDateAdmin} (${nights} Malam)\n*Estimasi Total:* ${formatCurrency(dynamicTotal)}`;

    let message = "";
    if (lang === 'id') {
      message = `Halo Ndalem Mataram,\n\nSaya berminat untuk mereservasi kamar berikut:\n\n*Tipe Kamar:* ${room.name_id}\n*Jadwal:* ${inDateAdmin} s.d. ${outDateAdmin} (${nights} Malam)\n*Estimasi Total:* ${formatCurrency(dynamicTotal)}\n\nMohon bantuannya untuk mengecek ketersediaan dan melanjutkan proses reservasi ini. Terima kasih.`;
    } else if (lang === 'zh') {
      message = `你好 Ndalem Mataram，\n\n我有意预订以下客房：\n\n*房型:* ${name}\n*日期:* ${inDateGuest} 至 ${outDateGuest} (${nights} 晚)\n*预计总价:* ${formatCurrency(dynamicTotal)}\n\n请协助查询空房情况并继续预订流程。谢谢。` + adminCopy;
    } else {
      message = `Hello Ndalem Mataram,\n\nI am interested in reserving the following room:\n\n*Room Type:* ${name}\n*Dates:* ${inDateGuest} to ${outDateGuest} (${nights} Nights)\n*Estimated Total:* ${formatCurrency(dynamicTotal)}\n\nCould you please help me check the availability and proceed with this reservation? Thank you.` + adminCopy;
    }
    
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Menggabungkan main_image dan gallery ke dalam satu array untuk galeri
  const allImages = [room.main_image, ...(room.gallery || [])].filter(Boolean); // Filter untuk membuang yang null/kosong

  return (
    <div id="main-content" className="bg-cream min-h-screen">
      <div className="pt-28 pb-4 px-6 max-w-6xl mx-auto">
        <Link to="/rooms" className="inline-flex items-center gap-2 text-primary-600 font-sans text-sm hover:text-primary-500 transition-colors">
          <ArrowLeft size={15} /> {lang === 'id' ? 'Kembali ke Daftar Kamar' : lang === 'zh' ? '返回房间列表' : 'Back to Rooms'}
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          
          <div className="lg:col-span-2 space-y-8">
            {/* ── BAGIAN GAMBAR GALERI ── */}
            <div className="space-y-4">
              {/* Gambar Utama */}
              <div className="relative rounded-2xl overflow-hidden h-80 md:h-[400px] bg-primary-100 shadow-md">
                <img
                  src={getImageUrl(selectedImage || room.main_image)}
                  alt={name}
                  className="w-full h-full object-cover transition-all duration-300"
                  onError={(e) => { e.target.src = '/assets/placeholder.jpg'; }}
                />
              </div>
              
              {/* Thumbnail Kecil (Hanya muncul jika foto lebih dari 1) */}
              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(img)}
                      className={`shrink-0 w-24 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 
                        ${(selectedImage || room.main_image) === img ? 'border-primary-600 shadow-md scale-105 opacity-100' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img src={getImageUrl(img)} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h1 className="font-display text-4xl text-dark font-bold mb-2">{name}</h1>
              <div className="flex flex-wrap gap-5 text-sm text-gray-500 font-sans mb-6">
                <span className="flex items-center gap-1.5"><Maximize2 size={14} className="text-primary-500" /> {room.size_sqm} m²</span>
                <span className="flex items-center gap-1.5"><Users size={14} className="text-primary-500" /> {room.capacity} {t('rooms.guests_label')}</span>
                <span className="flex items-center gap-1.5"><BedDouble size={14} className="text-primary-500" /> {bedType}</span>
              </div>
              <p className="text-gray-600 font-body text-lg leading-relaxed mb-8">{description}</p>
            </div>

            <div>
              <h2 className="font-display text-2xl text-dark font-bold mb-5">{t('rooms.amenities')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                {(room.amenities || []).map((amenity) => {
                  const Icon = AMENITY_MAP[amenity.icon] || Check; // Fallback ke check mark jika icon ga ketemu
                  return (
                    <div key={amenity.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
                      <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center">
                        <Icon size={16} className="text-primary-600" />
                      </div>
                      <span className="text-sm font-sans text-dark leading-tight">
                        {lang === 'zh' ? (amenity.name_zh || amenity.name_id) : (lang === 'en' ? amenity.name_en : amenity.name_id)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {features && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600 font-sans">
                      <Check size={14} className="text-primary-500 shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── SIDEBAR CTA (Sesuai Desain Klasik Asli dengan Terjemahan Valid) ── */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
              <div className="bg-amber-800 px-6 py-5 relative">
                {isHolidaySeason && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm tracking-wider">
                    {lang === 'id' ? 'MUSIM LIBURAN' : lang === 'zh' ? '旺季' : 'HIGH SEASON'}
                  </div>
                )}
                {isLowSeason && (
                  <div className="absolute top-4 right-4 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm tracking-wider">
                    {lang === 'id' ? 'HARGA SPESIAL' : lang === 'zh' ? '特价' : 'SPECIAL RATE'}
                  </div>
                )}
                <div className="text-amber-200 text-xs font-sans uppercase tracking-widest mb-1">
                  {lang === 'id' ? 'Rata-rata per malam' : lang === 'zh' ? '平均每晚' : 'Average per night'}
                </div>
                <div className="text-white font-display text-3xl font-bold">{formatCurrency(averagePerNight)}</div>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600 font-sans">
                  {lang === 'id' ? 'Tentukan tanggal rencana menginap Anda untuk mengecek harga dan ketersediaan.' : lang === 'zh' ? '请选择您计划的入住日期以查看价格和空房情况。' : 'Select your planned stay dates to check pricing and availability.'}
                </p>

                <div className="grid grid-cols-2 gap-3 pb-2">
                  <div>
                    <label className="block text-xs font-sans font-semibold text-amber-800 uppercase tracking-wider mb-1.5">{t('booking.checkin_date')}</label>
                    <div className="relative">
                      <Calendar size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-600 pointer-events-none z-10" />
                      <DatePicker
                        selected={localCheckin}
                        onChange={(d) => { setLocalCheckin(d); if (d >= localCheckout) setLocalCheckout(addDays(d, 1)); }}
                        minDate={new Date()}
                        dateFormat="dd/MM/yy"
                        className="w-full pl-7 pr-2 py-2 border border-gray-200 rounded-lg text-xs font-sans focus:outline-none focus:border-amber-600 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sans font-semibold text-amber-800 uppercase tracking-wider mb-1.5">{t('booking.checkout_date')}</label>
                    <div className="relative">
                      <Calendar size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-600 pointer-events-none z-10" />
                      <DatePicker
                        selected={localCheckout}
                        onChange={(d) => setLocalCheckout(d)}
                        minDate={addDays(localCheckin, 1)}
                        dateFormat="dd/MM/yy"
                        className="w-full pl-7 pr-2 py-2 border border-gray-200 rounded-lg text-xs font-sans focus:outline-none focus:border-amber-600 bg-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm font-sans">
                  <div className="flex justify-between text-gray-600">
                    <span>{nights} {t('common.nights')} {lang === 'id' ? 'Menginap' : lang === 'zh' ? '住宿' : 'Stay'}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-dark text-base">
                    <span>{t('booking.total_price')}</span>
                    <span className="text-amber-800">{formatCurrency(dynamicTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={handleWhatsAppBooking}
                  className="w-full py-3.5 bg-[#25D366] hover:bg-[#128C7E] text-white font-sans font-semibold rounded-xl transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {lang === 'id' ? 'Pesan via WhatsApp' : lang === 'zh' ? '通过 WhatsApp 预订' : 'Book via WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}